import { useState, useRef } from "react";

const DASHBOARD_API = process.env.REACT_APP_API_URL || "http://localhost:5050/api";

export default function TrackedUploadModal({ isOpen, onClose, onSubmit, accountName }) {
  const [description, setDescription] = useState("");
  const [file,        setFile]        = useState(null);
  const [fileLabel,   setFileLabel]   = useState("Accepts .xlsx or .csv");
  const [submitting,  setSubmitting]  = useState(false);
  const fileRef = useRef(null);

  function reset() {
    setDescription("");
    setFile(null);
    setFileLabel("Accepts .xlsx or .csv");
    setSubmitting(false);
    if (fileRef.current) fileRef.current.value = "";
  }

  function handleClose() {
    reset();
    onClose();
  }

  function onFileChange(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setFileLabel(`✓ ${f.name} (${(f.size / 1024).toFixed(1)} KB) ready`);
  }

  async function handleSubmit() {
    if (!file) { alert("Please select a file to upload."); return; }
    setSubmitting(true);
    try {
      await onSubmit({ file, description });
      reset();
    } catch {
      setSubmitting(false);
    }
  }

  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) handleClose();
  }

  return (
    <div className={`port-modal-overlay${isOpen ? " open" : ""}`} onMouseDown={handleOverlayClick}>
      <div className="port-modal">
        <div className="port-modal-hdr">
          <div>
            <div className="port-modal-title">Upload Positions</div>
            <div className="port-modal-subtitle">{accountName} · Updates the live tracked positions</div>
          </div>
          <button className="port-modal-close" onClick={handleClose}>✕</button>
        </div>

        <div className="port-modal-body">
          <div className="port-modal-alert port-modal-alert-tracked">
            <div className="port-modal-alert-icon port-modal-alert-icon-tracked">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
            </div>
            <div>
              <div className="port-modal-alert-title">This will update {accountName}'s live positions</div>
              <div className="port-modal-alert-sub">
                The uploaded file will replace the current manual snapshot and will be included in the{" "}
                <strong>next daily risk calculation</strong>. Existing broker feed data is not affected.
              </div>
            </div>
          </div>

          <div className="port-name-row">
            <label className="port-name-label">
              Description{" "}
              <span style={{ color: "var(--dash-muted)", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
                (optional)
              </span>
            </label>
            <textarea
              className="port-name-input"
              rows={2}
              placeholder="e.g. End-of-month rebalance following Q1 earnings. Reduced tech overweight…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="port-step-row">
            <div className="port-step-num port-step-num-gold">1</div>
            <div className="port-step-body">
              <div className="port-step-title">Download the input template</div>
              <div className="port-step-sub">Fill Tab 1 (Positions), Tab 2 (Alternatives), Tab 3 (Parameters)</div>
            </div>
            <a
              href={`${DASHBOARD_API}/download_file/input_template.xlsx?token=${sessionStorage.getItem("token")}`}
              className="port-step-action port-step-action-gold"
            >
              ↓ Template
            </a>
          </div>

          <div className="port-step-row">
            <div className="port-step-num port-step-num-blue">2</div>
            <div className="port-step-body">
              <div className="port-step-title">Upload completed file</div>
              <div className={`port-step-sub${file ? " ok" : ""}`}>{fileLabel}</div>
            </div>
            <label className="port-step-action port-step-action-blue" style={{ cursor: "pointer" }}>
              ↑ Browse
              <input
                type="file"
                accept=".xlsx,.csv"
                style={{ display: "none" }}
                ref={fileRef}
                onChange={onFileChange}
              />
            </label>
          </div>
        </div>

        <div className="port-modal-footer">
          <button className="port-btn port-btn-ghost" onClick={handleClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="port-btn port-btn-primary"
            onClick={handleSubmit}
            disabled={submitting || !file}
          >
            {submitting ? "Uploading…" : "Submit Positions"}
          </button>
        </div>
      </div>
    </div>
  );
}
